import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ArcElement,
  CategoryScale,
  LinearScale,
  Title,
  Tooltip,
  Legend
} from 'chart.js';

ChartJS.register(
  ArcElement,
  CategoryScale,
  LinearScale,
  Title,
  Tooltip,
  Legend
);

function ExecutiveSummaryDashboard() {
  const navigate = useNavigate();

  // Mock data
  const stats = {
    modelInventory: {
      total: 142,
      active: 126,
      retired: 16
    },
    tasks: {
      total: 26,
      active: 9,
      pastDue: 6,
      upcoming: 3,
      completed: 8
    },
    openFindings: {
      total: 5,
      high: 1,
      medium: 2,
      low: 2
    }
  };

  const upcomingModels = [
    { dueDate: '04/05/2026', development: 'Jane Doe Engine', owner: 'John Doe', status: 'Assigned' },
    { dueDate: '04/17/2026', development: 'Quarterly Performance', owner: 'In Progress', status: 'In Progress' },
    { dueDate: '04/18/2026', development: 'Stresstest/Vasdi', owner: 'Assigned', status: 'Assigned' },
    { dueDate: '04/20/2026', development: 'Vendor Model Assessment', owner: 'Assigned', status: 'Assigned' }
  ];

  const modelValidation = [
    {
      id: 'M-018',
      finding: 'Validation findings',
      model: 'Credit Risk Model',
      owner: 'Completed',
      riskLevel: 'High'
    },
    {
      id: 'M-010',
      finding: 'Outdated assumptions',
      model: 'Fraud Detection',
      owner: 'In Progress',
      riskLevel: 'Medium'
    },
    {
      id: 'M-012',
      finding: 'Missing documenta',
      model: 'Interest Rate Model',
      owner: 'In Progress',
      riskLevel: 'Medium'
    },
    {
      id: 'M-016',
      finding: 'Data quality concerns',
      model: 'Trading Strategy Model 1',
      owner: 'Low',
      riskLevel: 'Low'
    }
  ];

  // Task donut chart
  const taskChartData = {
    labels: ['Active', 'Past Due', 'Upcoming', 'Completed'],
    datasets: [{
      data: [stats.tasks.active, stats.tasks.pastDue, stats.tasks.upcoming, stats.tasks.completed],
      backgroundColor: ['#4472C4', '#ED7D31', '#FFC000', '#70AD47'],
      borderWidth: 2,
      borderColor: '#fff'
    }]
  };

  const taskChartOptions = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            return context.label + ': ' + context.parsed;
          }
        }
      }
    }
  };

  return (
    <div className="container-fluid">
      {/* Header */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h2>
                <i className="bi bi-graph-up me-2"></i>
                Executive Summary Dashboard
              </h2>
              <p className="text-muted mb-0">Model Risk Management - Executive Overview</p>
            </div>
            <button className="btn btn-outline-secondary" onClick={() => navigate('/reports')}>
              <i className="bi bi-arrow-left me-2"></i>
              Quay lại
            </button>
          </div>
        </div>
      </div>

      {/* Top Stats Cards */}
      <div className="row g-4 mb-4">
        {/* Model Inventory */}
        <div className="col-md-4">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body">
              <h5 className="card-title text-muted mb-3">Model Inventory</h5>
              <div className="text-center">
                <h1 className="display-3 fw-bold text-primary">{stats.modelInventory.total}</h1>
                <div className="d-flex justify-content-around mt-3">
                  <div>
                    <p className="mb-0 text-muted small">Active Models</p>
                    <h5 className="fw-bold text-success">{stats.modelInventory.active}</h5>
                  </div>
                  <div>
                    <p className="mb-0 text-muted small">Retired Models</p>
                    <h5 className="fw-bold text-secondary">{stats.modelInventory.retired}</h5>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tasks */}
        <div className="col-md-4">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body">
              <h5 className="card-title text-muted mb-3">Tasks</h5>
              <div className="d-flex align-items-center justify-content-center" style={{ height: '200px' }}>
                <div style={{ width: '150px', height: '150px', position: 'relative' }}>
                  <Doughnut data={taskChartData} options={taskChartOptions} />
                  <div style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    textAlign: 'center'
                  }}>
                    <h2 className="mb-0 fw-bold">{stats.tasks.total}</h2>
                  </div>
                </div>
              </div>
              <div className="row mt-3 text-center small">
                <div className="col-3">
                  <span className="badge bg-primary">{stats.tasks.active}</span>
                  <p className="mb-0 mt-1">Active</p>
                </div>
                <div className="col-3">
                  <span className="badge bg-warning text-dark">{stats.tasks.pastDue}</span>
                  <p className="mb-0 mt-1">Past Due</p>
                </div>
                <div className="col-3">
                  <span className="badge bg-info">{stats.tasks.upcoming}</span>
                  <p className="mb-0 mt-1">Upcoming</p>
                </div>
                <div className="col-3">
                  <span className="badge bg-success">{stats.tasks.completed}</span>
                  <p className="mb-0 mt-1">Completed</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Open Findings */}
        <div className="col-md-4">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body">
              <h5 className="card-title text-muted mb-3">Open Findings</h5>
              <div className="text-center">
                <h1 className="display-3 fw-bold text-danger">{stats.openFindings.total}</h1>
                <div className="mt-4">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span className="text-muted">High</span>
                    <div className="d-flex align-items-center">
                      <div className="progress" style={{ width: '100px', height: '20px' }}>
                        <div className="progress-bar bg-danger" style={{ width: `${(stats.openFindings.high / stats.openFindings.total) * 100}%` }}></div>
                      </div>
                      <span className="ms-2 fw-bold">{stats.openFindings.high}</span>
                    </div>
                  </div>
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span className="text-muted">Medium</span>
                    <div className="d-flex align-items-center">
                      <div className="progress" style={{ width: '100px', height: '20px' }}>
                        <div className="progress-bar bg-warning" style={{ width: `${(stats.openFindings.medium / stats.openFindings.total) * 100}%` }}></div>
                      </div>
                      <span className="ms-2 fw-bold">{stats.openFindings.medium}</span>
                    </div>
                  </div>
                  <div className="d-flex justify-content-between align-items-center">
                    <span className="text-muted">Low</span>
                    <div className="d-flex align-items-center">
                      <div className="progress" style={{ width: '100px', height: '20px' }}>
                        <div className="progress-bar bg-success" style={{ width: `${(stats.openFindings.low / stats.openFindings.total) * 100}%` }}></div>
                      </div>
                      <span className="ms-2 fw-bold">{stats.openFindings.low}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tables */}
      <div className="row g-4">
        {/* Upcoming Models */}
        <div className="col-lg-6">
          <div className="card border-0 shadow-sm">
            <div className="card-header bg-white border-bottom">
              <h5 className="mb-0">
                <i className="bi bi-calendar-check me-2"></i>
                Upcoming Models
              </h5>
            </div>
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-hover mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Due Date</th>
                      <th>Development</th>
                      <th>Owner</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {upcomingModels.map((model, index) => (
                      <tr key={index}>
                        <td>{model.dueDate}</td>
                        <td>{model.development}</td>
                        <td>{model.owner}</td>
                        <td>
                          <span className={`badge ${
                            model.status === 'Assigned' ? 'bg-info' : 'bg-warning'
                          }`}>
                            {model.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        {/* Model Validation */}
        <div className="col-lg-6">
          <div className="card border-0 shadow-sm">
            <div className="card-header bg-white border-bottom">
              <h5 className="mb-0">
                <i className="bi bi-check-circle me-2"></i>
                Model Validation
              </h5>
            </div>
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-hover mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>ID</th>
                      <th>Finding</th>
                      <th>Model</th>
                      <th>Owner</th>
                      <th>Risk Level</th>
                    </tr>
                  </thead>
                  <tbody>
                    {modelValidation.map((item, index) => (
                      <tr key={index}>
                        <td className="fw-bold">{item.id}</td>
                        <td>{item.finding}</td>
                        <td>{item.model}</td>
                        <td>{item.owner}</td>
                        <td>
                          <span className={`badge ${
                            item.riskLevel === 'High' ? 'bg-danger' :
                            item.riskLevel === 'Medium' ? 'bg-warning text-dark' :
                            'bg-success'
                          }`}>
                            {item.riskLevel}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ExecutiveSummaryDashboard;
